import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type * as React from "react";

import { cn } from "@/lib/utils";

/** C25 批1:右侧抽屉单一实现(收编 DrawerPrimitive 手写×3 + DialogContent 改样式×3)。
 * slide-in 180ms(index.css .ui-drawer,尊重 reduce-motion);头部=标题+badges+✕ 统一。
 * 后台页面对齐批 2:内容区默认 p-4(与头部 px-4 同一条边线)、块间 16;
 * 通栏布局(多栏 / 自带分栏与条带)传 `flush` 不加内边距;内容分区用 `DrawerSection`。 */
export function Drawer({
	open,
	onClose,
	title,
	description,
	badges,
	width = "min(1240px,95vw)",
	testId,
	flush = false,
	children,
}: {
	open: boolean;
	onClose: () => void;
	title: React.ReactNode;
	description?: React.ReactNode;
	badges?: React.ReactNode;
	width?: string;
	testId?: string;
	/** 通栏布局:内容区不加内边距,由内容自己排(运行轨迹三栏、配置版本对比、一次试跑) */
	flush?: boolean;
	children: React.ReactNode;
}) {
	return (
		<DialogPrimitive.Root open={open} onOpenChange={(o) => (o ? undefined : onClose())}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="ui-overlay fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
				<DialogPrimitive.Content
					className="ui-drawer fixed right-0 top-0 z-50 flex h-full flex-col border-l border-surface-border bg-surface-background shadow-xl focus:outline-none"
					style={{ width }}
					data-testid={testId}
				>
					<header className="flex items-center justify-between gap-3 border-b border-surface-border bg-surface-card px-4 py-3">
						<div className="min-w-0 space-y-0.5">
							<div className="flex min-w-0 flex-wrap items-center gap-2">
								<DialogPrimitive.Title className="truncate text-sm font-semibold text-text-primary">
									{title}
								</DialogPrimitive.Title>
								{badges}
							</div>
							{description ? (
								<DialogPrimitive.Description className="text-xs text-text-muted">
									{description}
								</DialogPrimitive.Description>
							) : null}
						</div>
						<DialogPrimitive.Close
							className={cn(
								"flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-text-secondary transition-colors duration-150 hover:bg-surface-subtle hover:text-text-primary",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-state-focus",
							)}
							aria-label="关闭"
						>
							<X className="h-4 w-4" />
						</DialogPrimitive.Close>
					</header>
					<div className={cn("min-h-0 flex-1 overflow-y-auto", !flush && "space-y-4 p-4")}>
						{children}
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}

/** 抽屉内的分区框:白卡片放在抽屉灰底上(容器档 rounded-lg,component-standards §二);可带小标题。 */
export function DrawerSection({
	title,
	className,
	testId,
	children,
}: {
	title?: React.ReactNode;
	className?: string;
	testId?: string;
	children: React.ReactNode;
}) {
	return (
		<section
			className={cn(
				"space-y-3 rounded-lg border border-surface-border bg-surface-card p-4",
				className,
			)}
			data-testid={testId}
		>
			{title ? <h3 className="text-sm font-semibold text-text-primary">{title}</h3> : null}
			{children}
		</section>
	);
}
